import React from 'react';
import { Search } from 'lucide-react';

import { EntityDropDownFilterContainer } from 'components/entityViewer/entitiesDropDownFilter/EntityDropDownFilterContainer';

import { DisplayOptionsDropdown } from './DisplayOptionsDropdown';

/**
 * FilterBar
 *
 * Internal component used by SplitPanelSelector.
 * Renders the top filter/search bar with optional search input, display options, and filter dropdowns.
 * Wraps existing EntityDropDownFilterContainer
 *
 * Responsibilities:
 * - Provide search input (if onSearch callback is provided)
 * - Display options dropdown for sorting/view preferences
 * - Render filter dropdowns using EntityDropDownFilterContainer
 *
 * @param {Object} props
 * @param {Object} [props.filterConfigs] - Configuration for filter dropdowns.
 *   Passed directly to EntityDropDownFilterContainer.
 *   Shape: { filters: Array<{ name, label, type, options, ... }> }
 * @param {Object} [props.displayConfig] - Configuration for display options dropdown.
 *   Passed to DisplayOptionsDropdown component for sorting, view mode, etc.
 * @param {Function} [props.onSearch] - Callback when search input changes.
 *   If not provided, search input is not rendered.
 *   Signature: (searchTerm: string) => void
 * @param {Function} [props.onFilterChange] - Callback when filter values change.
 *   Passed to EntityDropDownFilterContainer.
 *   Signature: (filterValues: Object) => void
 */
export function FilterBar({ displayConfig, filterConfigs = [], onFilterChange, onSearch }) {
    return (
        <div>
            <div className="flex gap-2 items-center">
                {/* Search Input */}
                {onSearch && (
                    <div className="relative w-80 ml-2">
                        <Search
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                            size={16}
                        />
                        <input
                            type="text"
                            placeholder="Search..."
                            onChange={(e) => onSearch(e.target.value)}
                            className="w-full h-9 pl-9 pr-4 bg-white border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                )}

                {/* Display Options Dropdown */}
                {displayConfig && <DisplayOptionsDropdown {...displayConfig} />}

                {filterConfigs && (
                    <EntityDropDownFilterContainer
                        filterConfigs={filterConfigs}
                        onChange={onFilterChange}
                        initialValues={[]}
                        autoApply
                    />
                )}
            </div>
        </div>
    );
}
